import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { DossierDropbox } from '../../../../lib/connecteurs.js';
import styles from './Connecteur.module.css';

/** Deux niveaux : les dossiers de la racine, puis ceux qu'ils contiennent. */
function arborescence(dossiers: readonly DossierDropbox[]): {
  racines: DossierDropbox[];
  enfants: Map<string, DossierDropbox[]>;
} {
  const chemins = new Set(dossiers.map((dossier) => dossier.chemin));
  const enfants = new Map<string, DossierDropbox[]>();
  const racines: DossierDropbox[] = [];
  for (const dossier of dossiers) {
    // Un dossier dont le parent n'est pas listé (liste tronquée) monte à la racine.
    if (dossier.parent === null || !chemins.has(dossier.parent)) racines.push(dossier);
    else enfants.set(dossier.parent, [...(enfants.get(dossier.parent) ?? []), dossier]);
  }
  return { racines, enfants };
}

/**
 * Le dossier Dropbox à importer : « tout le compte », ou un dossier de la
 * racine, ou l'un de ses sous-dossiers. Un seul choix, au clavier comme à la
 * souris (cases d'option natives).
 */
export function ChoixDossier({
  dossiers,
  valeur,
  onChoisir,
}: {
  dossiers: readonly DossierDropbox[];
  /** Le chemin choisi ; `''` : tout le compte ; `null` : rien encore. */
  valeur: string | null;
  onChoisir: (chemin: string) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const nom = useId();
  const { racines, enfants } = arborescence(dossiers);

  const option = (chemin: string, libelle: string, aide?: string): JSX.Element => (
    <label className={styles.choix}>
      <input
        type="radio"
        name={nom}
        value={chemin}
        checked={valeur === chemin}
        onChange={() => onChoisir(chemin)}
      />
      <span className={styles.choixNom}>
        {libelle}
        {aide ? <span className={styles.choixAide}>{aide}</span> : null}
      </span>
    </label>
  );

  return (
    <fieldset className={styles.arbre}>
      <legend>{t('importDropbox.dossier.groupe')}</legend>
      <ul className={styles.arbreListe}>
        <li>{option('', t('importDropbox.dossier.tout'), t('importDropbox.dossier.toutAide'))}</li>
        {racines.map((racine) => {
          const sous = enfants.get(racine.chemin) ?? [];
          return (
            <li key={racine.chemin}>
              {option(racine.chemin, racine.nom)}
              {sous.length > 0 ? (
                <ul className={styles.arbreListe}>
                  {sous.map((enfant) => (
                    <li key={enfant.chemin}>{option(enfant.chemin, enfant.nom)}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
    </fieldset>
  );
}
